import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Clock, X as IconX } from "lucide-react";

/**
 * Lightweight day + time-slot picker with zero new dependencies.
 *
 * Renders inside a popover beneath the trigger. Designed for proposing
 * interview slots — far cleaner than the browser-native `datetime-local`
 * which varies across browsers and looks dated.
 *
 * Props:
 *   value      string | ""  — ISO-local "YYYY-MM-DDTHH:mm" form value
 *   onChange   (val: string) => void
 *   minDate    Date         — disables days before this (default: today)
 *   slotMinutes number      — granularity of time slots (default 30)
 *   testIdPrefix string     — prefix for data-testid on each slot button
 */
export default function DateTimePicker({
  value = "",
  onChange,
  minDate,
  slotMinutes = 30,
  testIdPrefix = "dtp",
  placeholder = "Pick date & time",
}) {
  const today = useMemo(() => {
    const d = minDate ? new Date(minDate) : new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, [minDate]);

  // Parse the controlled value back into a Date object (or null)
  const selected = useMemo(() => {
    if (!value) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }, [value]);

  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => {
    const d = selected || new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  // Build the calendar grid: 6 weeks × 7 days, padded with prev/next month days
  const grid = useMemo(() => {
    const firstDow = viewMonth.getDay();              // 0..6 (Sun..Sat)
    const start = new Date(viewMonth);
    start.setDate(1 - firstDow);
    const days = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      days.push(d);
    }
    return days;
  }, [viewMonth]);

  const slots = useMemo(() => {
    const arr = [];
    for (let h = 9; h < 20; h++) {                    // 9 AM → 8 PM working window
      for (let m = 0; m < 60; m += slotMinutes) arr.push([h, m]);
    }
    return arr;
  }, [slotMinutes]);

  const sameDay = (a, b) =>
    a && b && a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  const onPickDay = (d) => {
    // Preserve the current time-of-day if one was set; otherwise default 10:00
    const next = new Date(d);
    if (selected) {
      next.setHours(selected.getHours(), selected.getMinutes(), 0, 0);
    } else {
      next.setHours(10, 0, 0, 0);
    }
    onChange?.(toLocalISO(next));
  };

  const onPickSlot = (h, m) => {
    const base = selected || new Date(today);
    const next = new Date(base);
    next.setHours(h, m, 0, 0);
    onChange?.(toLocalISO(next));
    setOpen(false);
  };

  const clear = () => onChange?.("");

  const label = selected
    ? selected.toLocaleString(undefined, {
        weekday: "short", month: "short", day: "numeric",
        hour: "numeric", minute: "2-digit",
      })
    : placeholder;

  return (
    <div className="relative" data-testid={`${testIdPrefix}-wrap`}>
      <button
        type="button"
        className="input flex items-center justify-between text-left"
        onClick={() => setOpen((o) => !o)}
        data-testid={`${testIdPrefix}-trigger`}
        aria-expanded={open}
      >
        <span className={selected ? "" : "text-muted"}>{label}</span>
        <span className="inline-flex items-center gap-2">
          {selected && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); clear(); }}
              onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); clear(); } }}
              className="btn-ghost px-1 py-0.5"
              data-testid={`${testIdPrefix}-clear`}
              aria-label="Clear date"
            >
              <IconX size={12} />
            </span>
          )}
          <Clock size={12} className="text-muted" />
        </span>
      </button>

      {open && (
        <div
          className="absolute z-50 mt-1 bg-white border border-rule rounded-md shadow-card p-3 w-[320px]"
          data-testid={`${testIdPrefix}-popover`}
        >
          {/* Month nav */}
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              className="btn-ghost"
              onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1))}
              data-testid={`${testIdPrefix}-prev`}
              aria-label="Previous month"
            >
              <ChevronLeft size={14} />
            </button>
            <div className="font-medium text-sm">
              {viewMonth.toLocaleString(undefined, { month: "long", year: "numeric" })}
            </div>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1))}
              data-testid={`${testIdPrefix}-next`}
              aria-label="Next month"
            >
              <ChevronRight size={14} />
            </button>
          </div>

          {/* Day-of-week header */}
          <div className="grid grid-cols-7 text-[10px] uppercase text-muted mb-1">
            {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
              <div key={i} className="text-center py-1">{d}</div>
            ))}
          </div>

          {/* Day cells */}
          <div className="grid grid-cols-7 gap-0.5">
            {grid.map((d, i) => {
              const inMonth = d.getMonth() === viewMonth.getMonth();
              const isPast = d < today;
              const isSel = sameDay(d, selected);
              const isToday = sameDay(d, new Date());
              const cls = [
                "h-8 text-sm rounded-sm transition-colors",
                isPast ? "text-muted/40 cursor-not-allowed" : "hover:bg-paper cursor-pointer",
                !inMonth && !isPast ? "text-muted" : "",
                isSel ? "bg-default text-white hover:bg-default" : "",
                isToday && !isSel ? "ring-1 ring-default/40" : "",
              ].join(" ");
              return (
                <button
                  key={i}
                  type="button"
                  disabled={isPast}
                  className={cls}
                  onClick={() => !isPast && onPickDay(d)}
                  data-testid={`${testIdPrefix}-day-${d.toISOString().slice(0, 10)}`}
                >
                  {d.getDate()}
                </button>
              );
            })}
          </div>

          {/* Time slots */}
          <div className="mt-3 border-t border-rule pt-3">
            <div className="eyebrow flex items-center gap-1 mb-1.5">
              <Clock size={10} /> Time slot
            </div>
            <div
              className="grid grid-cols-4 gap-1 max-h-32 overflow-y-auto pr-1"
              data-testid={`${testIdPrefix}-slots`}
            >
              {slots.map(([h, m]) => {
                const lbl = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
                const active = selected && selected.getHours() === h && selected.getMinutes() === m;
                return (
                  <button
                    key={`${h}-${m}`}
                    type="button"
                    className={[
                      "text-xs rounded-sm py-1 border font-medium",
                      active
                        ? "bg-default text-white border-default ring-2 ring-default/40"
                        : "border-rule hover:bg-paper text-default",
                    ].join(" ")}
                    onClick={() => onPickSlot(h, m)}
                    data-testid={`${testIdPrefix}-slot-${lbl.replace(":", "")}`}
                  >
                    {lbl}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Convert a Date to a local-tz "YYYY-MM-DDTHH:mm" string (datetime-local format). */
function toLocalISO(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
